import React, { useEffect, useState } from 'react';
import { getHealthSettings, saveHealthSettings, getBankAccounts } from '../../services/api';

const FIELDS = [
  ['unreconciledWarnDays', 'Unreconciled items: review after (days)'],
  ['unreconciledFailDays', 'Unreconciled items: action needed after (days)'],
  ['oldUnpaidDays', 'Old unpaid invoices/bills: overdue by more than (days)'],
  ['oldCreditDays', 'Old credit notes: older than (days)'],
  ['unapprovedDays', 'Unapproved invoices/bills: older than (days)'],
  ['inactiveContactMonths', 'Inactive contacts: unused for (months, max 12)'],
  ['lowCostAssetThreshold', 'Capitalisation threshold — flag fixed assets under (£)'],
  ['capitalItemThreshold', 'Capital item review — flag P&L costs over (£)'],
  ['misallocatedThreshold', 'Misallocated items — flag catch-all items over (£)'],
  ['openingBalanceTolerance', 'Opening balance difference tolerance (£)'],
  ['vatThreshold', 'VAT registration threshold (£)'],
  ['valuationMultiple', 'Business valuation EBITDA multiple'],
  ['salesTargetGrowthPct', 'Sales target: growth on last year (%)'],
  ['monthlySalesTarget', 'Sales target: fixed monthly amount (£, blank = use last year)']
];

export default function HealthSettings({ tenantId }) {
  const [form, setForm] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setForm(null); setError(null); setStatus(null);
    getHealthSettings(tenantId).then(s => {
      if (!live) return;
      const f = {};
      for (const [k] of FIELDS) f[k] = s[k] === null || s[k] === undefined ? '' : String(s[k]);
      f.obYearEnd = s.openingBalance?.yearEnd || '';
      f.obNetAssets = s.openingBalance ? String(s.openingBalance.netAssets) : '';
      f.bank = {};
      for (const b of s.bankBalances || []) f.bank[b.accountId] = { date: b.date, balance: String(b.balance) };
      setForm(f);
    }).catch(err => live && setError(err.response?.data?.error || 'Could not load settings'));
    getBankAccounts(tenantId).then(a => live && setBankAccounts(a || [])).catch(() => {});
    return () => { live = false; };
  }, [tenantId]);

  if (error && !form) return <div className="error-state"><div className="error-msg">{error}</div></div>;
  if (!form) return <div className="running-state"><div className="spinner" /></div>;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setBank = (id, k, v) => setForm(f => ({ ...f, bank: { ...f.bank, [id]: { ...(f.bank[id] || {}), [k]: v } } }));

  async function save() {
    setError(null); setStatus(null);
    const settings = {};
    for (const [k] of FIELDS) settings[k] = form[k] === '' ? null : Number(form[k]);
    if (form.obYearEnd || form.obNetAssets) {
      if (!form.obYearEnd || form.obNetAssets === '') { setError('Enter both the year end date and the filed net assets, or clear both.'); return; }
      settings.openingBalance = { yearEnd: form.obYearEnd, netAssets: Number(form.obNetAssets) };
    } else {
      settings.openingBalance = null;
    }
    settings.bankBalances = Object.entries(form.bank)
      .filter(([, b]) => b.date && b.balance !== '' && b.balance !== undefined)
      .map(([accountId, b]) => ({ accountId, date: b.date, balance: Number(b.balance) }));
    try {
      await saveHealthSettings(tenantId, settings);
      setStatus('Saved. Run the health check again to apply.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save settings');
    }
  }

  return (
    <div className="hs-form">
      <section>
        <h3 className="hc-group-title">Bank balance check</h3>
        <p className="rules-sub">Enter each account's balance from the bank statement. The check compares it with Xero's balance on the same date.</p>
        {bankAccounts.length === 0 && <p className="rules-sub">No bank accounts found.</p>}
        {bankAccounts.map(a => (
          <div key={a.id} className="hs-row">
            <label>{a.name}</label>
            <input type="date" value={form.bank[a.id]?.date || ''} onChange={e => setBank(a.id, 'date', e.target.value)} />
            <input type="number" step="0.01" placeholder="Statement balance" value={form.bank[a.id]?.balance ?? ''}
              onChange={e => setBank(a.id, 'balance', e.target.value)} />
          </div>
        ))}
      </section>

      <section>
        <h3 className="hc-group-title">Opening balance check</h3>
        <p className="rules-sub">From the last accounts filed at Companies House: the balance sheet date and net assets (use a minus sign for net liabilities).</p>
        <div className="hs-row">
          <label>Year end / net assets</label>
          <input type="date" value={form.obYearEnd} onChange={e => set('obYearEnd', e.target.value)} />
          <input type="number" step="0.01" placeholder="Net assets (£)" value={form.obNetAssets} onChange={e => set('obNetAssets', e.target.value)} />
        </div>
      </section>

      <section>
        <h3 className="hc-group-title">Thresholds</h3>
        {FIELDS.map(([k, label]) => (
          <div key={k} className="hs-row">
            <label htmlFor={`hs-${k}`}>{label}</label>
            <input id={`hs-${k}`} type="number" step="any" value={form[k]} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
      </section>

      {error && <div className="hc-note">{error}</div>}
      {status && <div className="hc-note hc-note-muted">{status}</div>}
      <button className="btn-primary" onClick={save}>Save settings</button>
    </div>
  );
}
